import React, { useEffect, useMemo, useRef, useState } from 'react';

import Tippy from '@tippyjs/react';
import cs from 'classnames';

import { iaRa } from 'lib/helpers/utility';

import Counter from '../Counter/Counter';
import Icon from '../Icon/Icon';

import ActionWrapper from './components/ActionWrapper/ActionWrapper';
import { DEFAULT_ACTIONS } from './lib/actions';

const ExpandableToolbar = ({
  id,
  actions = [],
  fetchData = () => {},
  category,
  className,
  actionContentClassName,
  setIsToolbarOpen = () => {},
  activityProps,
  overlay = false,
  defaultOpenAction
}) => {
  const componentRef = useRef(null);
  const lastAutoOpenedKey = useRef(null);

  const [selectedAction, setSelectedAction] = useState(null);

  const allActions = useMemo(
    () =>
      [...DEFAULT_ACTIONS({ activityProps }), ...actions].sort(
        (a, b) => (a.sortIndex || 999) - (b.sortIndex || 999)
      ),
    [actions, activityProps]
  );

  useEffect(() => {
    if (!defaultOpenAction) return;

    const autoOpenKey = `${id}:${defaultOpenAction}`;
    if (lastAutoOpenedKey.current === autoOpenKey) return;

    const foundAction = allActions.find((a) => a.id === defaultOpenAction);
    if (foundAction) {
      setSelectedAction(foundAction);
      lastAutoOpenedKey.current = autoOpenKey;
    }
  }, [id, defaultOpenAction, allActions]);

  const activeAction = useMemo(() => {
    return allActions.find((action) => action.id === selectedAction?.id);
  }, [allActions, selectedAction?.id]);

  const onActionToggle = (action) => {
    setSelectedAction((prevAction) => (prevAction?.id === action?.id ? null : action));
  };

  useEffect(() => {
    setIsToolbarOpen(!!activeAction);
  }, [activeAction, setIsToolbarOpen]);

  const handleExportPdf = async () => {
    if (componentRef.current?.handleExportPdf) {
      await componentRef.current.handleExportPdf();
    }
  };

  return (
    <div className={cs('relative flex h-full flex-row-reverse', className)}>
      <div className="z-20 h-full min-w-14 border-l border-solid border-white border-l-neutral-200 bg-white pt-6">
        <div className="flex flex-col items-center gap-6">
          {iaRa(allActions)
            .filter((action) => !action.hidden)
            .map((action) => (
              <Tippy key={action.id} content={action.name} placement="left" className="tippy-dark">
                <div
                  className={cs(
                    'relative flex h-8 w-8 cursor-pointer items-center justify-center rounded',
                    action.color && 'hover:bg-neutral-100',
                    activeAction?.id === action.id && 'bg-neutral-100'
                  )}
                  data-qa={action?.name}
                  onClick={() => onActionToggle(action)}>
                  <Counter count={action.count}>
                    <Icon icon={action.icon} className="cursor-pointer" size={24} />
                  </Counter>
                </div>
              </Tippy>
            ))}
        </div>
      </div>

      <ActionWrapper
        isOpen={!!activeAction}
        title={activeAction?.name}
        hasExport={activeAction?.hasExport}
        handleExportPdf={handleExportPdf}
        overlay={overlay}
        actionContentClassName={actionContentClassName}
        onClose={() => setSelectedAction(null)}>
        {activeAction &&
          React.cloneElement(activeAction.component, {
            ...activeAction,
            id,
            fetchData,
            category,
            ref: componentRef
          })}
      </ActionWrapper>
    </div>
  );
};

export default ExpandableToolbar;
